import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotification } from '@/components/NotificationProvider';
import { usePermissions } from '@/components/PermissionsProvider';
import { Plus, MoreHorizontal, Pencil, Trash2, Plane, Award, Stethoscope, ShieldCheck, BarChart3, Calendar, X, Check, ChevronsUpDown, Globe, Home, History, ClipboardCheck, DollarSign, RotateCcw, AlertTriangle, CheckSquare, Square, CreditCard, ArrowLeft } from 'lucide-react';
import { formatDateForInput, formatDisplayDate } from '@/utils/dateUtils';
import { AuditTrailPanel, LastModifiedBadge } from '@/components/AuditTrailPanel';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useTableView, TableViewToolbar, ColumnFilterRow, SortableHead } from '@/components/TableViewManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Common countries list
const COUNTRIES = [
  'Afghan', 'Albanian', 'Algerian', 'American', 'Andorran', 'Angolan', 'Argentine', 'Armenian', 'Australian', 'Austrian',
  'Azerbaijani', 'Bahamian', 'Bahraini', 'Bangladeshi', 'Barbadian', 'Belarusian', 'Belgian', 'Belizean', 'Beninese', 'Bhutanese',
  'Bolivian', 'Bosnian', 'Brazilian', 'British', 'Bruneian', 'Bulgarian', 'Burkinabe', 'Burmese', 'Burundian', 'Cambodian',
  'Cameroonian', 'Canadian', 'Cape Verdean', 'Central African', 'Chadian', 'Chilean', 'Chinese', 'Colombian', 'Comorian', 'Congolese',
  'Costa Rican', 'Croatian', 'Cuban', 'Cypriot', 'Czech', 'Danish', 'Djiboutian', 'Dominican', 'Dutch', 'Ecuadorian',
  'Egyptian', 'Emirati', 'English', 'Equatorial Guinean', 'Eritrean', 'Estonian', 'Ethiopian', 'Fijian', 'Filipino', 'Finnish',
  'French', 'Gabonese', 'Gambian', 'Georgian', 'German', 'Ghanaian', 'Greek', 'Grenadian', 'Guatemalan', 'Guinean',
  'Guyanese', 'Haitian', 'Honduran', 'Hungarian', 'Icelandic', 'Indian', 'Indonesian', 'Iranian', 'Iraqi', 'Irish',
  'Israeli', 'Italian', 'Ivorian', 'Jamaican', 'Japanese', 'Jordanian', 'Kazakh', 'Kenyan', 'Kiribati', 'Korean',
  'Kuwaiti', 'Kyrgyz', 'Laotian', 'Latvian', 'Lebanese', 'Liberian', 'Libyan', 'Lithuanian', 'Luxembourgish', 'Macedonian',
  'Malagasy', 'Malawian', 'Malaysian', 'Maldivian', 'Malian', 'Maltese', 'Mauritanian', 'Mauritian', 'Mexican', 'Moldovan',
  'Monegasque', 'Mongolian', 'Montenegrin', 'Moroccan', 'Mozambican', 'Namibian', 'Nepalese', 'New Zealander', 'Nicaraguan', 'Nigerian',
  'Norwegian', 'Omani', 'Pakistani', 'Palauan', 'Palestinian', 'Panamanian', 'Papua New Guinean', 'Paraguayan', 'Peruvian', 'Polish',
  'Portuguese', 'Qatari', 'Romanian', 'Russian', 'Rwandan', 'Saint Lucian', 'Salvadoran', 'Samoan', 'Saudi', 'Scottish',
  'Senegalese', 'Serbian', 'Seychellois', 'Sierra Leonean', 'Singaporean', 'Slovak', 'Slovenian', 'Somali', 'South African', 'Spanish',
  'Sri Lankan', 'Sudanese', 'Surinamese', 'Swazi', 'Swedish', 'Swiss', 'Syrian', 'Taiwanese', 'Tajik', 'Tanzanian',
  'Thai', 'Togolese', 'Tongan', 'Trinidadian', 'Tunisian', 'Turkish', 'Turkmen', 'Ugandan', 'Ukrainian', 'Uruguayan',
  'Uzbek', 'Vanuatuan', 'Venezuelan', 'Vietnamese', 'Welsh', 'Yemeni', 'Zambian', 'Zimbabwean'
];

// Common languages list
const LANGUAGES = [
  'Afrikaans', 'Albanian', 'Amharic', 'Arabic', 'Armenian', 'Azerbaijani', 'Basque', 'Belarusian', 'Bengali', 'Bosnian',
  'Bulgarian', 'Burmese', 'Catalan', 'Cebuano', 'Chinese (Cantonese)', 'Chinese (Mandarin)', 'Croatian', 'Czech', 'Danish', 'Dutch',
  'English', 'Estonian', 'Filipino', 'Finnish', 'French', 'Galician', 'Georgian', 'German', 'Greek', 'Gujarati',
  'Haitian Creole', 'Hausa', 'Hebrew', 'Hindi', 'Hungarian', 'Icelandic', 'Igbo', 'Indonesian', 'Irish', 'Italian',
  'Japanese', 'Javanese', 'Kannada', 'Kazakh', 'Khmer', 'Korean', 'Kurdish', 'Kyrgyz', 'Lao', 'Latin',
  'Latvian', 'Lithuanian', 'Luxembourgish', 'Macedonian', 'Malagasy', 'Malay', 'Malayalam', 'Maltese', 'Maori', 'Marathi',
  'Mongolian', 'Nepali', 'Norwegian', 'Pashto', 'Persian', 'Polish', 'Portuguese', 'Punjabi', 'Romanian', 'Russian',
  'Samoan', 'Scottish Gaelic', 'Serbian', 'Shona', 'Sindhi', 'Sinhala', 'Slovak', 'Slovenian', 'Somali', 'Spanish',
  'Sundanese', 'Swahili', 'Swedish', 'Tajik', 'Tamil', 'Telugu', 'Thai', 'Turkish', 'Ukrainian', 'Urdu',
  'Uzbek', 'Vietnamese', 'Welsh', 'Xhosa', 'Yiddish', 'Yoruba', 'Zulu'
];

// Common airports for FIFO operations (Australia focused with international)
const AIRPORTS = [
  { code: 'PER', name: 'Perth' },
  { code: 'PHE', name: 'Port Hedland' },
  { code: 'KTA', name: 'Karratha' },
  { code: 'BME', name: 'Broome' },
  { code: 'NTL', name: 'Newman' },
  { code: 'LEA', name: 'Learmonth' },
  { code: 'ASP', name: 'Alice Springs' },
  { code: 'DRW', name: 'Darwin' },
  { code: 'CNS', name: 'Cairns' },
  { code: 'TSV', name: 'Townsville' },
  { code: 'MKY', name: 'Mackay' },
  { code: 'ROK', name: 'Rockhampton' },
  { code: 'BNE', name: 'Brisbane' },
  { code: 'SYD', name: 'Sydney' },
  { code: 'MEL', name: 'Melbourne' },
  { code: 'ADL', name: 'Adelaide' },
  { code: 'OOL', name: 'Gold Coast' },
  { code: 'CBR', name: 'Canberra' },
  { code: 'HBA', name: 'Hobart' },
  { code: 'KGI', name: 'Kalgoorlie' },
  { code: 'GET', name: 'Geraldton' },
  { code: 'EPR', name: 'Esperance' },
  { code: 'ABY', name: 'Albany' },
  { code: 'MNG', name: 'Maningrida' },
  { code: 'GOV', name: 'Gove' },
  { code: 'WEI', name: 'Weipa' },
  { code: 'ISA', name: 'Mount Isa' },
  // International
  { code: 'SIN', name: 'Singapore' },
  { code: 'KUL', name: 'Kuala Lumpur' },
  { code: 'JKT', name: 'Jakarta' },
  { code: 'MNL', name: 'Manila' },
  { code: 'BKK', name: 'Bangkok' },
  { code: 'HKG', name: 'Hong Kong' },
  { code: 'DXB', name: 'Dubai' },
  { code: 'DOH', name: 'Doha' },
  { code: 'AKL', name: 'Auckland' },
  { code: 'CHC', name: 'Christchurch' },
  { code: 'WLG', name: 'Wellington' },
  { code: 'POM', name: 'Port Moresby' },
  { code: 'DIL', name: 'Dili' },
];

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function Personnel({ user }) {
  const notification = useNotification();
  const navigate = useNavigate();
  const [personnel, setPersonnel] = useState([]);
  // Server-side pagination ("load more") state
  const PAGE_SIZE = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [visaSummary, setVisaSummary] = useState([]);
  const reloadRef = useRef(() => {});
  const sentinelRef = useRef(null);
  const [roles, setRoles] = useState([]);
  const [rotations, setRotations] = useState([]);
  const [certTypes, setCertTypes] = useState([]);
  const [inductionTypes, setInductionTypes] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    reporting_manager: '',
    contract_type: '',
    employer: '',
    department: '',
    skills: '',
    visa_type: '',
    visa_expiry: '',
    rotation_roster: '',
    next_rotation_start: '',
    next_rotation_end: '',
    nationalities: [],
    languages: [],
    current_allocation: '',
    ff_number: '',
    notes_remarks: ''
  });
  
  // Popover states for multi-select
  const [nationalityOpen, setNationalityOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  
  // FIFO Records
  const [fifoRecords, setFifoRecords] = useState([]);
  const [fifoFilterTab, setFifoFilterTab] = useState('all');
  const [showPastFlights, setShowPastFlights] = useState(false);
  const [fifoFormData, setFifoFormData] = useState({ 
    id: '', 
    flight_type: 'Domestic',
    fly_in_date: '', 
    fly_in_flight: '',
    fly_in_from: '',
    fly_in_to: '',
    fly_in_time: '',
    fly_out_date: '', 
    fly_out_flight: '',
    fly_out_from: '',
    fly_out_to: '',
    fly_out_time: '',
    project: '', 
    location: '', 
    status: 'Planned',
    notes: '' 
  });
  const [editingFifo, setEditingFifo] = useState(null);
  const [showFifoForm, setShowFifoForm] = useState(false);
  
  // Certifications
  const [certifications, setCertifications] = useState([]);
  const [certFormData, setCertFormData] = useState({ id: '', type: '', name: '', number: '', issuer: '', training_start_date: '', training_finish_date: '', issue_date: '', expiry_date: '', status: '', notes: '' });
  const [editingCert, setEditingCert] = useState(null);
  const [showCertForm, setShowCertForm] = useState(false);
  
  // Medical Records (flat fields)
  const [medFormData, setMedFormData] = useState({ pem_date: '', labdas_date: '', pem_result: '', labdas_result: '', project_specific_pem: false, project_specific_pem_project: '' });

  // Project-specific PEMs list
  const [projectPems, setProjectPems] = useState([]);
  const [pemFormData, setPemFormData] = useState({ id: '', project: '', pem_date: '', result: '', notes: '' });
  const [showPemForm, setShowPemForm] = useState(false);
  const [editingPem, setEditingPem] = useState(null);
  
  // Inductions
  const [inductions, setInductions] = useState([]);
  const [indFormData, setIndFormData] = useState({ id: '', site: '', induction_type: 'General', completed_date: '', expiry_date: '', status: 'Active', notes: '' });
  const [editingInd, setEditingInd] = useState(null);
  const [showIndForm, setShowIndForm] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showTrash, setShowTrash] = useState(false);
  const [trashedPersonnel, setTrashedPersonnel] = useState([]);

  // Sales rates state
  const [salesRates, setSalesRates] = useState([]);
  const [salesFormData, setSalesFormData] = useState({ id: '', rate_type: 'Daily', rate_value: '', currency: 'AUD', effective_from: '', effective_to: '', notes: '' });
  const [editingSalesRate, setEditingSalesRate] = useState(null);
  const [showSalesForm, setShowSalesForm] = useState(false);

  // Role history state
  const [roleHistory, setRoleHistory] = useState([]);
  const [roleHistoryFormData, setRoleHistoryFormData] = useState({ id: '', role: '', start_date: '', end_date: '', notes: '' });
  const [editingRoleHistory, setEditingRoleHistory] = useState(null);
  const [showRoleHistoryForm, setShowRoleHistoryForm] = useState(false);

  // Visa history state (mirrors role history pattern)
  const [visaHistory, setVisaHistory] = useState([]);
  const [visaHistoryFormData, setVisaHistoryFormData] = useState({ id: '', visa_type: '', country: '', document_number: '', start_date: '', expiry_date: '', notes: '' });
  const [editingVisaHistory, setEditingVisaHistory] = useState(null);
  const [showVisaHistoryForm, setShowVisaHistoryForm] = useState(false);

  // Derive the "current" role from role history: prefer entries without end_date, then latest start_date
  const computeCurrentRole = (history) => {
    if (!Array.isArray(history) || history.length === 0) return '';
    const sorted = [...history].sort((a, b) => {
      const aEnd = a.end_date ? new Date(a.end_date).getTime() : Infinity;
      const bEnd = b.end_date ? new Date(b.end_date).getTime() : Infinity;
      if (aEnd !== bEnd) return bEnd - aEnd;
      const aStart = a.start_date ? new Date(a.start_date).getTime() : 0;
      const bStart = b.start_date ? new Date(b.start_date).getTime() : 0;
      return bStart - aStart;
    });
    return sorted[0]?.role || '';
  };

  // Derive the "current" visa from visa history: latest expiry_date, tiebreaker = latest start_date
  const computeCurrentVisa = (history) => {
    if (!Array.isArray(history) || history.length === 0) return null;
    const sorted = [...history].sort((a, b) => {
      const aExp = a.expiry_date ? new Date(a.expiry_date).getTime() : 0;
      const bExp = b.expiry_date ? new Date(b.expiry_date).getTime() : 0;
      if (aExp !== bExp) return bExp - aExp;
      const aStart = a.start_date ? new Date(a.start_date).getTime() : 0;
      const bStart = b.start_date ? new Date(b.start_date).getTime() : 0;
      return bStart - aStart;
    });
    return sorted[0] || null;
  };

  // Keep General tab's Role field in sync with the current role from Role History
  useEffect(() => {
    const current = computeCurrentRole(roleHistory);
    if (current) {
      setFormData(prev => (prev.role === current ? prev : { ...prev, role: current }));
    }
  }, [roleHistory]);

  // Keep General tab's Visa Type & Visa Expiry in sync with the current entry in Visa History
  useEffect(() => {
    const current = computeCurrentVisa(visaHistory);
    if (current) {
      const newType = current.visa_type || '';
      const newExpiry = current.expiry_date ? new Date(current.expiry_date).toISOString().slice(0, 10) : '';
      setFormData(prev => {
        if (prev.visa_type === newType && prev.visa_expiry === newExpiry) return prev;
        return { ...prev, visa_type: newType, visa_expiry: newExpiry };
      });
    }
  }, [visaHistory]);

  useEffect(() => {
    fetchRoles();
    fetchRotations();
    fetchCertTypes();
    fetchInductionTypes();
    fetchContractTypes();
    fetchVisaTypes();
    fetchEmployers();
    fetchDepartments();
    fetchProjects();
    fetchAllocations();
    fetchTrashedPersonnel();
    fetchVisaSummary();
  }, []);

  useEffect(() => {
    if (showTrash) fetchTrashedPersonnel();
  }, [showTrash]);

  const fetchVisaSummary = async () => {
    try {
      const res = await axios.get(`${API}/personnel/visa-summary`, { headers: getAuthHeader() });
      setVisaSummary(res.data);
    } catch (error) { /* Silently fail */ }
  };

  // Reload page 1 with current filters (used by mutation handlers).
  const fetchPersonnel = useCallback(() => reloadRef.current(), []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=personnel_role`, { headers: getAuthHeader() });
      setRoles(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchRotations = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=rotation`, { headers: getAuthHeader() });
      setRotations(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchCertTypes = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=certification_type`, { headers: getAuthHeader() });
      setCertTypes(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchInductionTypes = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=induction_type`, { headers: getAuthHeader() });
      setInductionTypes(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchContractTypes = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=contract_type`, { headers: getAuthHeader() });
      setContractTypes(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchVisaTypes = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=visa_type`, { headers: getAuthHeader() });
      setVisaTypes(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchEmployers = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=ownership_entity`, { headers: getAuthHeader() });
      setEmployers(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=department`, { headers: getAuthHeader() });
      setDepartments(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { headers: getAuthHeader() });
      setAllProjects(response.data);
      setProjects(response.data.filter(p => p.is_active));
    } catch (error) { /* Silently fail */ }
  };

  const fetchAllocations = async () => {
    try {
      const response = await axios.get(`${API}/allocations/personnel`, { headers: getAuthHeader() });
      setAllocations(response.data);
    } catch (error) { /* Silently fail */ }
  };

  const fetchTrashedPersonnel = async () => {
    try {
      const response = await axios.get(`${API}/personnel/trash/list`, { headers: getAuthHeader() });
      setTrashedPersonnel(response.data);
    } catch (error) { notification.error('Failed to load trash'); }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    notification.confirm(`Move ${selectedIds.size} personnel to trash?`, async () => {
      try {
        await axios.post(`${API}/personnel/bulk-delete`, { ids: Array.from(selectedIds) }, { headers: getAuthHeader() });
        notification.success(`${selectedIds.size} personnel moved to trash`);
        setSelectedIds(new Set());
        fetchPersonnel();
      } catch (error) {
        notification.error(error.response?.data?.detail || 'Bulk delete failed');
      }
    });
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/personnel/${id}/restore`, {}, { headers: getAuthHeader() });
      notification.success('Personnel restored');
      fetchTrashedPersonnel();
      fetchPersonnel();
    } catch (error) { notification.error('Restore failed'); }
  };

  const handlePermanentDelete = (id) => {
    notification.confirm('Permanently delete this personnel? This cannot be undone.', async () => {
      try {
        await axios.delete(`${API}/personnel/${id}/permanent`, { headers: getAuthHeader() });
        notification.success('Personnel permanently deleted');
        fetchTrashedPersonnel();
      } catch (error) { notification.error('Permanent delete failed'); }
    });
  };

  const handleEmptyTrash = () => {
    notification.confirm(`Permanently delete ALL ${trashedPersonnel.length} personnel in trash? This cannot be undone.`, async () => {
      try {
        await axios.post(`${API}/personnel/trash/empty`, {}, { headers: getAuthHeader() });
        notification.success('Trash emptied');
        setTrashedPersonnel([]);
      } catch (error) { notification.error('Empty trash failed'); }
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredPersonnel.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPersonnel.map(p => p.id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  // Get current allocation for a person
  const getCurrentAllocation = (personId) => {
    const today = new Date();
    const activeAlloc = allocations.find(a => 
      a.personnel_id === personId && 
      a.status === 'active' &&
      new Date(a.start_date) <= today &&
      new Date(a.end_date) >= today
    );
    if (activeAlloc) {
      const project = allProjects.find(p => p.id === activeAlloc.project_id);
      return project?.name || 'Assigned';
    }
    return 'Not Assigned';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        role: formData.role,
        reporting_manager: formData.reporting_manager || null,
        contract_type: formData.contract_type || null,
        employer: formData.employer || null,
        department: formData.department || null,
        skills: formData.skills.split(',').map(s => s.trim()).filter(s => s),
        visa_type: formData.visa_type || null,
        visa_expiry: formData.visa_expiry ? new Date(formData.visa_expiry).toISOString() : null,
        rotation_roster: formData.rotation_roster || null,
        next_rotation_start: formData.next_rotation_start ? new Date(formData.next_rotation_start).toISOString() : null,
        next_rotation_end: formData.next_rotation_end ? new Date(formData.next_rotation_end).toISOString() : null,
        nationality: formData.nationalities.length > 0 ? formData.nationalities.join(', ') : null,
        languages: formData.languages,
        current_allocation: formData.current_allocation || null,
        ff_number: formData.ff_number || null,
        notes_remarks: formData.notes_remarks || null,
        fifo_dates: fifoRecords.map(f => ({
          id: f.id || undefined,
          flight_type: f.flight_type || 'Domestic',
          fly_in_date: f.fly_in_date ? new Date(f.fly_in_date).toISOString() : null,
          fly_out_date: f.fly_out_date ? new Date(f.fly_out_date).toISOString() : null,
          fly_in_flight: f.fly_in_flight || null,
          fly_in_from: f.fly_in_from || null,
          fly_in_to: f.fly_in_to || null,
          fly_in_time: f.fly_in_time || null,
          fly_out_flight: f.fly_out_flight || null,
          fly_out_from: f.fly_out_from || null,
          fly_out_to: f.fly_out_to || null,
          fly_out_time: f.fly_out_time || null,
          project: f.project || null,
          location: f.location || null,
          status: f.status || 'Planned',
          notes: f.notes || null
        })),
        certifications: certifications.map(c => ({
          id: c.id || undefined,
          type: c.type,
          name: c.name,
          number: c.number || null,
          issuer: c.issuer || null,
          training_start_date: c.training_start_date ? new Date(c.training_start_date).toISOString() : null,
          training_finish_date: c.training_finish_date ? new Date(c.training_finish_date).toISOString() : null,
          issue_date: c.issue_date ? new Date(c.issue_date).toISOString() : null,
          expiry_date: c.expiry_date ? new Date(c.expiry_date).toISOString() : null,
          status: computeCertStatus(c.expiry_date) || null,
          notes: c.notes || null
        })),
        medical_records: [],
        pem_date: medFormData.pem_date ? new Date(medFormData.pem_date).toISOString() : null,
        labdas_date: medFormData.labdas_date ? new Date(medFormData.labdas_date).toISOString() : null,
        pem_result: medFormData.pem_result || null,
        labdas_result: medFormData.labdas_result || null,
        project_specific_pem: medFormData.project_specific_pem || false,
        project_specific_pem_project: medFormData.project_specific_pem ? (medFormData.project_specific_pem_project || null) : null,
        project_pems: projectPems.map(p => ({
          id: p.id || undefined,
          project: p.project,
          pem_date: p.pem_date ? new Date(p.pem_date).toISOString() : null,
          result: p.result || null,
          notes: p.notes || null,
        })),
        inductions: inductions.map(i => ({
          id: i.id || undefined,
          site: i.site,
          induction_type: i.induction_type || 'General',
          completed_date: i.completed_date ? new Date(i.completed_date).toISOString() : null,
          expiry_date: i.expiry_date ? new Date(i.expiry_date).toISOString() : null,
          status: i.status || 'Active',
          notes: i.notes || null
        })),
        sales_rates: salesRates.map(s => ({
          id: s.id || undefined,
          rate_type: s.rate_type || 'Daily',
          rate_value: parseFloat(s.rate_value) || 0,
          currency: s.currency || 'AUD',
          effective_from: s.effective_from ? new Date(s.effective_from).toISOString() : null,
          effective_to: s.effective_to ? new Date(s.effective_to).toISOString() : null,
          notes: s.notes || null
        })),
        role_history: roleHistory.map(r => ({
          id: r.id || undefined,
          role: r.role,
          start_date: r.start_date ? new Date(r.start_date).toISOString() : null,
          end_date: r.end_date ? new Date(r.end_date).toISOString() : null,
          notes: r.notes || null
        })),
        visa_history: visaHistory.map(v => ({
          id: v.id || undefined,
          visa_type: v.visa_type,
          country: v.country || null,
          document_number: v.document_number || null,
          start_date: v.start_date ? new Date(v.start_date).toISOString() : null,
          expiry_date: v.expiry_date ? new Date(v.expiry_date).toISOString() : null,
          notes: v.notes || null
        })),
        version: editingItem?.version || null
      };

      if (editingItem) {
        await axios.put(`${API}/personnel/${editingItem.id}`, data, { headers: getAuthHeader() });
        notification.success('Personnel updated successfully');
      } else {
        await axios.post(`${API}/personnel`, data, { headers: getAuthHeader() });
        notification.success('Personnel created successfully');
      }
      setOpen(false);
      setEditingItem(null);
      resetForm();
      fetchPersonnel();
    } catch (error) {
      if (error.response?.status === 409) {
        notification.conflict(() => {
          setOpen(false);
          setEditingItem(null);
          resetForm();
          fetchPersonnel();
        });
      } else {
        notification.error(error.response?.data?.detail || 'Operation failed');
      }
    }
  };

  const handleDelete = (id) => {
    notification.confirm('Are you sure you want to delete this personnel?', async () => {
      try {
        await axios.delete(`${API}/personnel/${id}`, { headers: getAuthHeader() });
        notification.success('Personnel deleted successfully');
        fetchPersonnel();
      } catch (error) {
        notification.error('Failed to delete personnel');
      }
    });
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    // Parse nationality - could be comma-separated string or array
    const nationalities = item.nationality 
      ? (typeof item.nationality === 'string' ? item.nationality.split(',').map(n => n.trim()).filter(n => n) : item.nationality)
      : [];
    setFormData({
      name: item.name,
      role: item.role,
      reporting_manager: item.reporting_manager || '',
      contract_type: item.contract_type || '',
      employer: item.employer || '',
      department: item.department || '',
      skills: item.skills.join(', '),
      visa_type: item.visa_type || '',
      visa_expiry: formatDateForInput(item.visa_expiry),
      rotation_roster: item.rotation_roster || '',
      next_rotation_start: formatDateForInput(item.next_rotation_start),
      next_rotation_end: formatDateForInput(item.next_rotation_end),
      nationalities: nationalities,
      languages: item.languages || [],
      current_allocation: getCurrentAllocation(item.id),
      ff_number: item.ff_number || '',
      notes_remarks: item.notes_remarks || ''
    });
    // Load FIFO records
    setFifoRecords((item.fifo_dates || []).map(f => ({
      id: f.id,
      flight_type: f.flight_type || 'Domestic',
      fly_in_date: formatDateForInput(f.fly_in_date),
      fly_in_flight: f.fly_in_flight || '',
      fly_in_from: f.fly_in_from || '',
      fly_in_to: f.fly_in_to || '',
      fly_in_time: f.fly_in_time || '',
      fly_out_date: formatDateForInput(f.fly_out_date),
      fly_out_flight: f.fly_out_flight || '',
      fly_out_from: f.fly_out_from || '',
      fly_out_to: f.fly_out_to || '',
      fly_out_time: f.fly_out_time || '',
      project: f.project || '',
      location: f.location || '',
      status: f.status || 'Planned',
      notes: f.notes || ''
    })));
    // Load certifications
    setCertifications((item.certifications || []).map(c => ({
      id: c.id,
      type: c.type || '',
      name: c.name || '',
      number: c.number || '',
      issuer: c.issuer || '',
      training_start_date: formatDateForInput(c.training_start_date),
      training_finish_date: formatDateForInput(c.training_finish_date),
      issue_date: formatDateForInput(c.issue_date),
      expiry_date: formatDateForInput(c.expiry_date),
      status: computeCertStatus(formatDateForInput(c.expiry_date)),
      notes: c.notes || ''
    })));
    // Load medical data
    setMedFormData({
      pem_date: formatDateForInput(item.pem_date),
      labdas_date: formatDateForInput(item.labdas_date),
      pem_result: item.pem_result || '',
      labdas_result: item.labdas_result || '',
      project_specific_pem: item.project_specific_pem || false,
      project_specific_pem_project: item.project_specific_pem_project || ''
    });
    // Load project-specific PEMs list
    setProjectPems((item.project_pems || []).map(p => ({
      id: p.id,
      project: p.project || '',
      pem_date: formatDateForInput(p.pem_date),
      result: p.result || '',
      notes: p.notes || ''
    })));
    // Load inductions
    setInductions((item.inductions || []).map(i => ({
      id: i.id,
      site: i.site || '',
      induction_type: i.induction_type || 'General',
      completed_date: formatDateForInput(i.completed_date),
      expiry_date: formatDateForInput(i.expiry_date),
      status: i.status || 'Active',
      notes: i.notes || ''
    })));
    // Load sales rates
    setSalesRates((item.sales_rates || []).map(s => ({
      id: s.id,
      rate_type: s.rate_type || 'Daily',
      rate_value: s.rate_value || '',
      currency: s.currency || 'AUD',
      effective_from: formatDateForInput(s.effective_from),
      effective_to: formatDateForInput(s.effective_to),
      notes: s.notes || ''
    })));
    // Load role history
    setRoleHistory((item.role_history || []).map(r => ({
      id: r.id,
      role: r.role || '',
      start_date: formatDateForInput(r.start_date),
      end_date: formatDateForInput(r.end_date),
      notes: r.notes || ''
    })));
    // Load visa history
    setVisaHistory((item.visa_history || []).map(v => ({
      id: v.id,
      visa_type: v.visa_type || '',
      country: v.country || '',
      document_number: v.document_number || '',
      start_date: formatDateForInput(v.start_date),
      expiry_date: formatDateForInput(v.expiry_date),
      notes: v.notes || ''
    })));
    setActiveTab('general');
    setOpen(true);
  };

  const handleRowClick = (item, e) => {
    if (e.target.closest('[data-actions-menu]')) return;
    handleEdit(item);
  };

  const resetForm = () => {
    setFormData({ name: '', role: '', reporting_manager: '', contract_type: '', employer: '', department: '', skills: '', visa_type: '', visa_expiry: '', rotation_roster: '', next_rotation_start: '', next_rotation_end: '', nationalities: [], languages: [], current_allocation: '', ff_number: '', notes_remarks: '' });
    setFifoRecords([]);
    setFifoFormData({ id: '', flight_type: 'Domestic', fly_in_date: '', fly_in_flight: '', fly_in_from: '', fly_in_to: '', fly_in_time: '', fly_out_date: '', fly_out_flight: '', fly_out_from: '', fly_out_to: '', fly_out_time: '', project: '', location: '', status: 'Planned', notes: '' });
    setEditingFifo(null);
    setShowFifoForm(false);
    setFifoFilterTab('all');
    setShowPastFlights(false);
    setCertifications([]);
    setCertFormData({ id: '', type: '', name: '', number: '', issuer: '', training_start_date: '', training_finish_date: '', issue_date: '', expiry_date: '', status: '', notes: '' });
    setEditingCert(null);
    setShowCertForm(false);
    setMedFormData({ pem_date: '', labdas_date: '', pem_result: '', labdas_result: '', project_specific_pem: false, project_specific_pem_project: '' });
    setProjectPems([]);
    setPemFormData({ id: '', project: '', pem_date: '', result: '', notes: '' });
    setEditingPem(null);
    setShowPemForm(false);
    setInductions([]);
    setIndFormData({ id: '', site: '', induction_type: 'General', completed_date: '', expiry_date: '', status: 'Active', notes: '' });
    setEditingInd(null);
    setShowIndForm(false);
    setSalesRates([]);
    setSalesFormData({ id: '', rate_type: 'Daily', rate_value: '', currency: 'AUD', effective_from: '', effective_to: '', notes: '' });
    setEditingSalesRate(null);
    setShowSalesForm(false);
    setRoleHistory([]);
    setRoleHistoryFormData({ id: '', role: '', start_date: '', end_date: '', notes: '' });
    setEditingRoleHistory(null);
    setShowRoleHistoryForm(false);
    setVisaHistory([]);
    setVisaHistoryFormData({ id: '', visa_type: '', country: '', document_number: '', start_date: '', expiry_date: '', notes: '' });
    setEditingVisaHistory(null);
    setShowVisaHistoryForm(false);
    setActiveTab('general');
    setNationalityOpen(false);
    setLanguageOpen(false);
  };

  // FIFO Record handlers
  const handleAddFifo = () => { 
    setEditingFifo(null); 
    setFifoFormData({ 
      id: '', 
      flight_type: 'Domestic',
      fly_in_date: '', 
      fly_in_flight: '',
      fly_in_from: '',
      fly_in_to: '',
      fly_in_time: '',
      fly_out_date: '', 
      fly_out_flight: '',
      fly_out_from: '',
      fly_out_to: '',
      fly_out_time: '',
      project: '', 
      location: '', 
      status: 'Planned',
      notes: '' 
    }); 
    setShowFifoForm(true); 
  };
  const handleEditFifo = (fifo) => { setEditingFifo(fifo); setFifoFormData({ ...fifo }); setShowFifoForm(true); };
  const handleDeleteFifo = (fifoId) => { setFifoRecords(fifoRecords.filter(f => f.id !== fifoId)); };
  const handleSaveFifo = () => {
    if (!fifoFormData.fly_in_date || !fifoFormData.fly_out_date) { notification.error('Fly In and Fly Out dates are required'); return; }
    if (editingFifo) { setFifoRecords(fifoRecords.map(f => f.id === editingFifo.id ? { ...fifoFormData } : f)); }
    else { setFifoRecords([...fifoRecords, { ...fifoFormData, id: `temp-${Date.now()}` }]); }
    setShowFifoForm(false); setEditingFifo(null);
  };
  
  // FIFO status badge colors
  const getFifoStatusBadge = (status) => {
    const variants = {
      'Planned': 'bg-blue-50 text-blue-700 border-blue-200',
      'Confirmed': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Completed': 'bg-slate-100 text-slate-600 border-slate-200',
      'Cancelled': 'bg-red-50 text-red-700 border-red-200'
    };
    return variants[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  // Certification handlers
  const handleAddCert = () => { setEditingCert(null); setCertFormData({ id: '', type: '', name: '', number: '', issuer: '', training_start_date: '', training_finish_date: '', issue_date: '', expiry_date: '', status: '', notes: '' }); setShowCertForm(true); };
  const handleEditCert = (cert) => { setEditingCert(cert); setCertFormData({ ...cert }); setShowCertForm(true); };
  const handleDeleteCert = (certId) => { setCertifications(certifications.filter(c => c.id !== certId)); };
  const handleSaveCert = () => {
    if (!certFormData.type || !certFormData.name) { notification.error('Type and Name are required'); return; }
    const derived = { ...certFormData, status: computeCertStatus(certFormData.expiry_date) };
    if (editingCert) { setCertifications(certifications.map(c => c.id === editingCert.id ? { ...derived } : c)); }
    else { setCertifications([...certifications, { ...derived, id: `temp-${Date.now()}` }]); }
    setShowCertForm(false); setEditingCert(null);
  };

  // Medical status helpers
  const getPemBadge = (result) => {
    if (result === 'green') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (result === 'orange') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (result === 'red') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-slate-50 text-slate-500 border-slate-200';
  };

  // Induction handlers
  const handleAddInd = () => { setEditingInd(null); setIndFormData({ id: '', site: '', induction_type: 'General', completed_date: '', expiry_date: '', status: 'Active', notes: '' }); setShowIndForm(true); };
  const handleEditInd = (ind) => { setEditingInd(ind); setIndFormData({ ...ind }); setShowIndForm(true); };
  const handleDeleteInd = (indId) => { setInductions(inductions.filter(i => i.id !== indId)); };
  const handleSaveInd = () => {
    if (!indFormData.site) { notification.error('Site is required'); return; }
    if (editingInd) { setInductions(inductions.map(i => i.id === editingInd.id ? { ...indFormData } : i)); }
    else { setInductions([...inductions, { ...indFormData, id: `temp-${Date.now()}` }]); }
    setShowIndForm(false); setEditingInd(null);
  };

  // Sales Rate handlers
  const handleAddSalesRate = () => { setEditingSalesRate(null); setSalesFormData({ id: '', rate_type: 'Daily', rate_value: '', currency: 'AUD', effective_from: '', effective_to: '', notes: '' }); setShowSalesForm(true); };
  const handleEditSalesRate = (rate) => { setEditingSalesRate(rate); setSalesFormData({ ...rate }); setShowSalesForm(true); };
  const handleDeleteSalesRate = (rateId) => { setSalesRates(salesRates.filter(r => r.id !== rateId)); };
  const handleSaveSalesRate = () => {
    if (!salesFormData.rate_value) { notification.error('Rate value is required'); return; }
    if (editingSalesRate) { setSalesRates(salesRates.map(r => r.id === editingSalesRate.id ? { ...salesFormData } : r)); }
    else { setSalesRates([...salesRates, { ...salesFormData, id: `temp-${Date.now()}` }]); }
    setShowSalesForm(false); setEditingSalesRate(null);
  };

  // Role History handlers
  const handleAddRoleHistory = () => { setEditingRoleHistory(null); setRoleHistoryFormData({ id: '', role: '', start_date: '', end_date: '', notes: '' }); setShowRoleHistoryForm(true); };
  const handleEditRoleHistory = (entry) => { setEditingRoleHistory(entry); setRoleHistoryFormData({ ...entry }); setShowRoleHistoryForm(true); };
  const handleDeleteRoleHistory = (entryId) => { setRoleHistory(roleHistory.filter(r => r.id !== entryId)); };
  const handleSaveRoleHistory = () => {
    if (!roleHistoryFormData.role) { notification.error('Role is required'); return; }
    if (editingRoleHistory) { setRoleHistory(roleHistory.map(r => r.id === editingRoleHistory.id ? { ...roleHistoryFormData } : r)); }
    else { setRoleHistory([...roleHistory, { ...roleHistoryFormData, id: `temp-${Date.now()}` }]); }
    setShowRoleHistoryForm(false); setEditingRoleHistory(null);
  };

  // Visa History handlers
  const handleAddVisaHistory = () => { setEditingVisaHistory(null); setVisaHistoryFormData({ id: '', visa_type: '', country: '', document_number: '', start_date: '', expiry_date: '', notes: '' }); setShowVisaHistoryForm(true); };
  const handleEditVisaHistory = (entry) => {
    setEditingVisaHistory(entry);
    setVisaHistoryFormData({
      ...entry,
      start_date: formatDateForInput(entry.start_date),
      expiry_date: formatDateForInput(entry.expiry_date),
    });
    setShowVisaHistoryForm(true);
  };
  const handleDeleteVisaHistory = (entryId) => { setVisaHistory(visaHistory.filter(v => v.id !== entryId)); };
  const handleSaveVisaHistory = () => {
    if (!visaHistoryFormData.visa_type) { notification.error('Visa type is required'); return; }
    if (editingVisaHistory) { setVisaHistory(visaHistory.map(v => v.id === editingVisaHistory.id ? { ...visaHistoryFormData } : v)); }
    else { setVisaHistory([...visaHistory, { ...visaHistoryFormData, id: `temp-${Date.now()}` }]); }
    setShowVisaHistoryForm(false); setEditingVisaHistory(null);
  };

  // Project-specific PEM handlers
  const handleAddPem = () => { setEditingPem(null); setPemFormData({ id: '', project: '', pem_date: '', result: '', notes: '' }); setShowPemForm(true); };
  const handleEditPem = (pem) => { setEditingPem(pem); setPemFormData({ ...pem }); setShowPemForm(true); };
  const handleDeletePem = (pemId) => { setProjectPems(projectPems.filter(p => p.id !== pemId)); };
  const handleSavePem = () => {
    if (!pemFormData.project) { notification.error('Project is required'); return; }
    if (editingPem) { setProjectPems(projectPems.map(p => p.id === editingPem.id ? { ...pemFormData } : p)); }
    else { setProjectPems([...projectPems, { ...pemFormData, id: `temp-${Date.now()}` }]); }
    setShowPemForm(false); setEditingPem(null);
  };

  const { canEdit: canEditModule, canView } = usePermissions();
  const canEdit = canEditModule('personnel_database');

  const roleOptions = useMemo(() => roles.map(r => r.name).sort(), [roles]);
  const employerOptions = useMemo(() => employers.map(e => e.name).sort(), [employers]);
  const contractTypeOptions = useMemo(() => contractTypes.map(c => c.name).sort(), [contractTypes]);
  const visaTypeOptions = useMemo(() => visaTypes.map(v => v.name).sort(), [visaTypes]);

  // Helper to compute current visa from a personnel record (mirrors Gantt + backend)
  const getCurrentVisaForRow = (item) => {
    const history = Array.isArray(item.visa_history) ? item.visa_history : [];
    if (history.length > 0) {
      const sorted = [...history].sort((a, b) => {
        const aExp = a.expiry_date ? new Date(a.expiry_date).getTime() : 0;
        const bExp = b.expiry_date ? new Date(b.expiry_date).getTime() : 0;
        if (aExp !== bExp) return bExp - aExp;
        const aStart = a.start_date ? new Date(a.start_date).getTime() : 0;
        const bStart = b.start_date ? new Date(b.start_date).getTime() : 0;
        return bStart - aStart;
      });
      return sorted[0];
    }
    if (item.visa_expiry) {
      return { visa_type: item.visa_type || 'Visa', expiry_date: item.visa_expiry, _legacy: true };
    }
    return null;
  };

  const getVisaColor = (expiryDate) => {
    if (!expiryDate) return 'bg-slate-300';
    const exp = new Date(expiryDate);
    const today = new Date();
    const days = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'bg-red-500';
    if (days <= 60) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  // Column definitions for TableViewManager
  const personnelColumns = useMemo(() => [
    { key: 'name', label: 'Name', filterable: true, filterType: 'text' },
    { key: 'role', label: 'Role', filterable: true, filterType: 'select', filterOptions: roleOptions },
    { key: 'employer', label: 'Employer', filterable: true, filterType: 'select', filterOptions: employerOptions },
    { key: 'contract_type', label: 'Contract Type', filterable: true, filterType: 'select', filterOptions: contractTypeOptions },
    { key: 'skills', label: 'Skills', filterable: true, filterType: 'text', accessor: (item) => (item.skills || []).join(', ') },
    { key: 'visa', label: 'Visa', filterable: true, filterType: 'select', filterOptions: visaTypeOptions, accessor: (item) => getCurrentVisaForRow(item)?.visa_type || '' },
    { key: 'next_fifo', label: 'Next FIFO', filterable: false },
    { key: 'compliance', label: 'Compliance', filterable: false },
    { key: 'current_allocation', label: 'Allocation', filterable: true, filterType: 'text' },
    { key: 'modified_by', label: 'Modified By', filterable: false },
    { key: 'actions', label: 'Actions', filterable: false },
  ], [roleOptions, employerOptions, contractTypeOptions, visaTypeOptions]);

  // Enrich each person with a LIVE-computed current_allocation derived from actual
  // active allocations (the stored personnel.current_allocation field is stale/blank
  // for records allocated via the Planner). Keeps the table column + filtering accurate.
  const personnelWithAllocation = useMemo(
    () => personnel.map(p => ({ ...p, current_allocation: getCurrentAllocation(p.id) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [personnel, allocations, allProjects]
  );

  const personnelTableView = useTableView({ pageKey: 'personnel', columns: personnelColumns, data: personnelWithAllocation });
  const filteredPersonnelRaw = personnelTableView.filteredData;

  // Quick filter: visa expiring within 30 days (incl. expired)
  const [visaExpiringFilter, setVisaExpiringFilter] = useState(false);
  // Accurate badge from the full-dataset visa summary (not just the loaded page)
  const visaExpiringSoonCount = useMemo(() => {
    const today = new Date();
    return visaSummary.filter(p => {
      const v = getCurrentVisaForRow(p);
      if (!v?.expiry_date) return false;
      const days = Math.ceil((new Date(v.expiry_date) - today) / (1000 * 60 * 60 * 24));
      return days <= 30;
    }).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visaSummary]);

  const filteredPersonnel = useMemo(() => {
    if (!visaExpiringFilter) return filteredPersonnelRaw;
    const today = new Date();
    return filteredPersonnelRaw.filter(p => {
      const v = getCurrentVisaForRow(p);
      if (!v?.expiry_date) return false;
      const days = Math.ceil((new Date(v.expiry_date) - today) / (1000 * 60 * 60 * 24));
      return days <= 30;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPersonnelRaw, visaExpiringFilter]);

  // ---- Server-side pagination ("load more") for the Personnel list ----
  // Filters map tableView column keys -> server params (stored-field filters only;
  // derived columns like visa/skills/allocation stay client-side on loaded rows).
  const personnelColumnFilterKey = JSON.stringify(personnelTableView.columnFilters);
  const buildPersonnelParams = useCallback((skip) => {
    const p = new URLSearchParams();
    p.set('skip', String(skip));
    p.set('limit', String(PAGE_SIZE));
    const cf = personnelTableView.columnFilters || {};
    const map = { name: 'name', role: 'role', employer: 'employer', contract_type: 'contract_type' };
    Object.entries(cf).forEach(([k, v]) => { if (v && map[k]) p.set(map[k], v); });
    if (personnelTableView.sortBy) { p.set('sort_by', personnelTableView.sortBy); p.set('sort_dir', personnelTableView.sortDir); }
    return p.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personnelColumnFilterKey, personnelTableView.sortBy, personnelTableView.sortDir]);

  const loadFirstPage = useCallback(async () => {
    setListLoading(true);
    fetchVisaSummary();
    try {
      if (visaExpiringFilter) {
        // Visa-expiring view: load the full set so the client-side derived-visa
        // filter is accurate across all personnel (occasional compliance action).
        const res = await axios.get(`${API}/personnel`, { headers: getAuthHeader() });
        setPersonnel(res.data);
        setTotalCount(res.data.length);
        setHasMore(false);
      } else {
        const res = await axios.get(`${API}/personnel/list?${buildPersonnelParams(0)}`, { headers: getAuthHeader() });
        setPersonnel(res.data.items);
        setTotalCount(res.data.total);
        setHasMore(res.data.has_more);
      }
    } catch (error) {
      notification.error('Failed to load personnel');
    }
    setListLoading(false);
  }, [buildPersonnelParams, visaExpiringFilter]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/personnel/list?${buildPersonnelParams(personnel.length)}`, { headers: getAuthHeader() });
      setPersonnel(prev => [...prev, ...res.data.items]);
      setTotalCount(res.data.total);
      setHasMore(res.data.has_more);
    } catch (error) {
      notification.error('Failed to load more personnel');
    }
    setLoadingMore(false);
  }, [buildPersonnelParams, personnel.length, hasMore, loadingMore]);

  useEffect(() => { reloadRef.current = loadFirstPage; }, [loadFirstPage]);

  // Deep-link from global search (?q=): prefill the Name column filter once on mount
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q');
    if (q) personnelTableView.setColumnFilter('name', q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { loadFirstPage(); }, 300);
    return () => clearTimeout(t);
  }, [loadFirstPage]);

  useEffect(() => {
    if (!hasMore || showTrash) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '250px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadMore, showTrash]);

  const personnelHasActiveFilters = personnelTableView.activeFilterCount > 0 || visaExpiringFilter;

  const getNextFifo = (item) => {
    const fifos = item.fifo_dates || [];
    if (fifos.length === 0) return null;
    const now = new Date();
    const upcoming = fifos.filter(f => new Date(f.fly_in_date) >= now).sort((a, b) => new Date(a.fly_in_date) - new Date(b.fly_in_date));
    return upcoming[0] || fifos[fifos.length - 1];
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Active': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Cleared': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Expired': 'bg-red-50 text-red-700 border-red-200',
      'Pending': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'Pending Renewal': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'Restrictions': 'bg-orange-50 text-orange-700 border-orange-200',
      'Pass': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Fail': 'bg-red-50 text-red-700 border-red-200',
      'Conditional': 'bg-yellow-50 text-yellow-700 border-yellow-200'
    };
    return variants[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  // Certificate status is fully derived from the expiry date:
  //   no expiry date  -> '' (empty, status left blank)
  //   expired (past)   -> 'Expired'
  //   within 30 days   -> 'Pending Renewal'
  //   otherwise        -> 'Active'
  const computeCertStatus = (expiryDate) => {
    if (!expiryDate) return '';
    const exp = new Date(expiryDate);
    if (isNaN(exp.getTime())) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Expired';
    if (days <= 30) return 'Pending Renewal';
    return 'Active';
  };

  // Compliance summary for table
  const getComplianceSummary = (item) => {
    const certs = (item.certifications || []).length;
    const inds = (item.inductions || []).length;
    return { certs, inds, total: certs + inds };
  };

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="personnel-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Personnel Database</h1>
          <p className="text-sm text-slate-600 mt-2">Manage workforce and personnel details</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => setAuditOpen(true)} 
            variant="outline"
            data-testid="personnel-audit-trail-button" 
            className="border-slate-300 hover:bg-slate-100 text-slate-700 shadow-sm rounded-md px-4 py-2 font-medium"
          >
            <History className="w-4 h-4 mr-2" />
            Activity Log
          </Button>
          <Button 
            onClick={() => navigate('/compliance')} 
            variant="outline"
            data-testid="compliance-matrix-button" 
            className="border-slate-300 hover:bg-slate-100 text-slate-700 shadow-sm rounded-md px-4 py-2 font-medium"
          >
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Compliance Matrix
          </Button>
          {canEdit && (
          <Sheet open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
            <SheetTrigger asChild>
              <Button onClick={() => { setEditingItem(null); resetForm(); }} data-testid="add-personnel-button" className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm rounded-md px-6 py-2 font-medium">
                <Plus className="w-4 h-4 mr-2" />
                Add Personnel
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-xl lg:max-w-3xl overflow-y-auto" data-testid="personnel-sheet">
              <SheetHeader>
                <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">
                  {editingItem ? 'Edit Personnel' : 'Add Personnel'}
                </SheetTitle>
                <SheetDescription>
                  {editingItem ? 'Update personnel details below' : 'Add new personnel to your workforce'}
                </SheetDescription>
              </SheetHeader>
              
              <form id="personnel-form" onSubmit={handleSubmit}>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                <TabsList className="grid w-full text-xs" style={{ gridTemplateColumns: `repeat(${[true, canView('personnel_database', 'certifications'), canView('personnel_database', 'medicals'), canView('personnel_database', 'inductions'), canView('personnel_database', 'sales'), true, true].filter(Boolean).length}, 1fr)` }}>
                  <TabsTrigger value="general" data-testid="general-tab">General</TabsTrigger>
                  {canView('personnel_database', 'certifications') && (
                  <TabsTrigger value="certifications" data-testid="certifications-tab">
                    <Award className="w-3 h-3 mr-1" />
                    Certs ({certifications.length})
                  </TabsTrigger>
                  )}
                  {canView('personnel_database', 'medicals') && (
                  <TabsTrigger value="medical" data-testid="medical-tab">
                    <Stethoscope className="w-3 h-3 mr-1" />
                    Medical
                  </TabsTrigger>
                  )}
                  {canView('personnel_database', 'inductions') && (
                  <TabsTrigger value="inductions" data-testid="inductions-tab">
                    <ShieldCheck className="w-3 h-3 mr-1" />
                    Inductions ({inductions.length})
                  </TabsTrigger>
                  )}
                  {canView('personnel_database', 'sales') && (
                  <TabsTrigger value="sales" data-testid="sales-tab">
                    <DollarSign className="w-3 h-3 mr-1" />
                    Sales ({salesRates.length})
                  </TabsTrigger>
                  )}
                  <TabsTrigger value="role_history" data-testid="role-history-tab">
                    <History className="w-3 h-3 mr-1" />
                    Roles ({roleHistory.length})
                  </TabsTrigger>
                  <TabsTrigger value="visa_history" data-testid="visa-history-tab">
                    <CreditCard className="w-3 h-3 mr-1" />
                    Visa ({visaHistory.length})
                  </TabsTrigger>
                </TabsList>
                
                {/* General Tab */}
                <TabsContent value="general">
                  <div className="py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-semibold text-slate-700">Name *</Label>
                        <Input id="name" data-testid="name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="role" className="text-sm font-semibold text-slate-700">
                          Role *
                          {roleHistory.length > 0 && (
                            <span className="ml-2 text-[10px] font-normal text-slate-500" data-testid="role-derived-hint">
                              (derived from current entry in Roles tab)
                            </span>
                          )}
                        </Label>
                        <select
                          id="role"
                          data-testid="role-select"
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          disabled={roleHistory.length > 0}
                          className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-700 disabled:cursor-not-allowed"
                          required
                          title={roleHistory.length > 0 ? 'Edit the current role from the Roles tab' : ''}
                        >
                          <option value="">Select Role</option>
                          {roles.map((r) => (<option key={r.id} value={r.name}>{r.name}</option>))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="reporting_manager" className="text-sm font-semibold text-slate-700">Reporting Manager</Label>
                        <Input id="reporting_manager" data-testid="reporting-manager-input" value={formData.reporting_manager} onChange={(e) => setFormData({ ...formData, reporting_manager: e.target.value })} placeholder="e.g., John Smith" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contract_type" className="text-sm font-semibold text-slate-700">Contract Type</Label>
                        <select id="contract_type" data-testid="contract-type-select" value={formData.contract_type} onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                          <option value="">Select Contract Type</option>
                          {contractTypes.map((ct) => (<option key={ct.id} value={ct.name}>{ct.name}</option>))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="employer" className="text-sm font-semibold text-slate-700">Employer</Label>
                        <select id="employer" data-testid="employer-select" value={formData.employer} onChange={(e) => setFormData({ ...formData, employer: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                          <option value="">Select Employer</option>
                          {employers.map((emp) => (<option key={emp.id} value={emp.name}>{emp.name}</option>))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="department" className="text-sm font-semibold text-slate-700">Department</Label>
                        <select id="department" data-testid="department-select" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                          <option value="">Select Department</option>
                          {departments.map((dep) => (<option key={dep.id} value={dep.name}>{dep.name}</option>))}
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="skills" className="text-sm font-semibold text-slate-700">Skills (comma-separated)</Label>
                        <Input id="skills" data-testid="skills-input" value={formData.skills} onChange={(e) => setFormData({ ...formData, skills: e.target.value })} placeholder="e.g., Welding, Heavy Equipment Operation" />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-sm font-semibold text-slate-700">Visas</Label>
                        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="visa-general-hint">
                          {visaHistory.length > 0
                            ? <>This person has <strong>{visaHistory.length}</strong> visa{visaHistory.length > 1 ? 's' : ''} on file. Manage them in the <strong>Visa</strong> tab.</>
                            : <>No visas on file. Add them in the <strong>Visa</strong> tab to track multiple co-existing visas.</>}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rotation_roster" className="text-sm font-semibold text-slate-700">Rotation/Roster</Label>
                        <select id="rotation_roster" data-testid="rotation-roster-select" value={formData.rotation_roster} onChange={(e) => {
                          const selectedRotation = rotations.find(r => r.name === e.target.value);
                          const newFormData = { ...formData, rotation_roster: e.target.value };
                          if (selectedRotation?.days_on && formData.next_rotation_start) {
                            const startDate = new Date(formData.next_rotation_start);
                            const endDate = new Date(startDate);
                            endDate.setDate(endDate.getDate() + selectedRotation.days_on - 1);
                            newFormData.next_rotation_end = endDate.toISOString().split('T')[0];
                          }
                          setFormData(newFormData);
                        }} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                          <option value="">Select Rotation</option>
                          {rotations.map((r) => (<option key={r.id} value={r.name}>{r.name}</option>))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="next_rotation_start" className="text-sm font-semibold text-slate-700">Next Rotation Start</Label>
                        <Input id="next_rotation_start" type="date" data-testid="next-rotation-start-input" value={formData.next_rotation_start} onChange={(e) => {
                          const startDate = e.target.value;
                          const selectedRotation = rotations.find(r => r.name === formData.rotation_roster);
                          const newFormData = { ...formData, next_rotation_start: startDate };
                          if (selectedRotation?.days_on && startDate) {
                            const start = new Date(startDate);
                            const endDate = new Date(start);
                            endDate.setDate(endDate.getDate() + selectedRotation.days_on - 1);
                            newFormData.next_rotation_end = endDate.toISOString().split('T')[0];
                          }
                          setFormData(newFormData);
                        }} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="next_rotation_end" className="text-sm font-semibold text-slate-700">Next Rotation End</Label>
                        <Input id="next_rotation_end" type="date" data-testid="next-rotation-end-input" value={formData.next_rotation_end} onChange={(e) => setFormData({ ...formData, next_rotation_end: e.target.value })} />
                        <p className="text-xs text-slate-500">Auto-calculated from rotation days, or set manually</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-slate-700">Nationality</Label>
                        <Popover open={nationalityOpen} onOpenChange={setNationalityOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={nationalityOpen}
                              className="w-full justify-between h-auto min-h-10 text-left font-normal"
                              data-testid="nationality-select"
                            >
                              <div className="flex flex-wrap gap-1">
                                {formData.nationalities.length === 0 ? (
                                  <span className="text-slate-500">Select nationalities...</span>
                                ) : (
                                  formData.nationalities.map((nat) => (
                                    <Badge key={nat} variant="secondary" className="mr-1 mb-1">
                                      {nat}
                                      <button
                                        type="button"
                                        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFormData({ ...formData, nationalities: formData.nationalities.filter(n => n !== nat) });
                                        }}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </Badge>
                                  ))
                                )}
                              </div>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search nationalities..." />
                              <CommandList>
                                <CommandEmpty>No nationality found.</CommandEmpty>
                                <CommandGroup className="max-h-64 overflow-auto">
                                  {COUNTRIES.map((country) => (
                                    <CommandItem
                                      key={country}
                                      value={country}
                                      onSelect={() => {
                                        const isSelected = formData.nationalities.includes(country);
                                        if (isSelected) {
                                          setFormData({ ...formData, nationalities: formData.nationalities.filter(n => n !== country) });
                                        } else {
                                          setFormData({ ...formData, nationalities: [...formData.nationalities, country] });
                                        }
                                      }}
                                    >
                                      <Check className={`mr-2 h-4 w-4 ${formData.nationalities.includes(country) ? 'opacity-100' : 'opacity-0'}`} />
                                      {country}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-slate-700">Languages</Label>
                        <Popover open={languageOpen} onOpenChange={setLanguageOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={languageOpen}
                              className="w-full justify-between h-auto min-h-10 text-left font-normal"
                              data-testid="languages-select"
                            >
                              <div className="flex flex-wrap gap-1">
                                {formData.languages.length === 0 ? (
                                  <span className="text-slate-500">Select languages...</span>
                                ) : (
                                  formData.languages.map((lang) => (
                                    <Badge key={lang} variant="secondary" className="mr-1 mb-1">
                                      {lang}
                                      <button
                                        type="button"
                                        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFormData({ ...formData, languages: formData.languages.filter(l => l !== lang) });
                                        }}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </Badge>
                                  ))
                                )}
                              </div>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search languages..." />
                              <CommandList>
                                <CommandEmpty>No language found.</CommandEmpty>
                                <CommandGroup className="max-h-64 overflow-auto">
                                  {LANGUAGES.map((language) => (
                                    <CommandItem
                                      key={language}
                                      value={language}
                                      onSelect={() => {
                                        const isSelected = formData.languages.includes(language);
                                        if (isSelected) {
                                          setFormData({ ...formData, languages: formData.languages.filter(l => l !== language) });
                                        } else {
                                          setFormData({ ...formData, languages: [...formData.languages, language] });
                                        }
                                      }}
                                    >
                                      <Check className={`mr-2 h-4 w-4 ${formData.languages.includes(language) ? 'opacity-100' : 'opacity-0'}`} />
                                      {language}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="current_allocation" className="text-sm font-semibold text-slate-700">Current Allocation</Label>
                        <div className={`w-full h-10 rounded-md border px-3 py-2 text-sm flex items-center ${
                          formData.current_allocation === 'Not Assigned' 
                            ? 'bg-slate-100 border-slate-200 text-slate-500' 
                            : 'bg-emerald-50 border-emerald-200 text-emerald-700 font-medium'
                        }`}>
                          {formData.current_allocation || 'Not Assigned'}
                        </div>
                        <p className="text-xs text-slate-500">Based on active project allocations</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ff_number" className="text-sm font-semibold text-slate-700">FF Number</Label>
                        <Input id="ff_number" data-testid="ff-number-input" value={formData.ff_number} onChange={(e) => setFormData({ ...formData, ff_number: e.target.value })} placeholder="Frequent Flyer number" />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="notes_remarks" className="text-sm font-semibold text-slate-700">Notes / Remarks</Label>
                        <Textarea id="notes_remarks" data-testid="notes-remarks-input" value={formData.notes_remarks} onChange={(e) => setFormData({ ...formData, notes_remarks: e.target.value })} placeholder="Any additional notes or remarks..." rows={3} />
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                {/* Certifications Tab */}
                <TabsContent value="certifications">
                  <div className="py-4">
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-sm text-slate-600">Manage certifications and licenses</p>
                      <Button type="button" size="sm" onClick={handleAddCert} data-testid="add-cert-button" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" />Add Certification</Button>
                    </div>
                    {showCertForm && (
                      <Card className="mb-4 border-blue-200 bg-blue-50">
                        <CardContent className="pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1"><Label className="text-xs font-semibold">Type *</Label><select value={certFormData.type} onChange={(e) => setCertFormData({ ...certFormData, type: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm" data-testid="cert-type-select"><option value="">Select Type</option>{certTypes.map(ct => <option key={ct.id} value={ct.name}>{ct.name}</option>)}<option value="Other">Other</option></select></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Name *</Label><Input value={certFormData.name} onChange={(e) => setCertFormData({ ...certFormData, name: e.target.value })} placeholder="e.g., Rigging Advanced" data-testid="cert-name-input" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Number</Label><Input value={certFormData.number} onChange={(e) => setCertFormData({ ...certFormData, number: e.target.value })} placeholder="License/Certificate number" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Issuer</Label><Input value={certFormData.issuer} onChange={(e) => setCertFormData({ ...certFormData, issuer: e.target.value })} placeholder="e.g., WorkSafe" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Training Start Date</Label><Input type="date" value={certFormData.training_start_date} onChange={(e) => setCertFormData({ ...certFormData, training_start_date: e.target.value })} data-testid="cert-training-start-input" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Training Finish Date</Label><Input type="date" value={certFormData.training_finish_date} onChange={(e) => setCertFormData({ ...certFormData, training_finish_date: e.target.value })} data-testid="cert-training-finish-input" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Issue Date</Label><Input type="date" value={certFormData.issue_date} onChange={(e) => setCertFormData({ ...certFormData, issue_date: e.target.value })} /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Expiry Date</Label><Input type="date" value={certFormData.expiry_date} onChange={(e) => setCertFormData({ ...certFormData, expiry_date: e.target.value })} data-testid="cert-expiry-input" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Status</Label><div className="w-full h-9 rounded-md border border-slate-200 bg-slate-50 px-3 py-1 text-sm flex items-center" data-testid="cert-status-derived">{computeCertStatus(certFormData.expiry_date) ? <Badge className={getStatusBadge(computeCertStatus(certFormData.expiry_date))}>{computeCertStatus(certFormData.expiry_date)}</Badge> : <span className="text-slate-400 text-xs italic">Set an expiry date to derive status</span>}</div><p className="text-[10px] text-slate-400">Auto-set from Expiry Date</p></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Notes</Label><Input value={certFormData.notes} onChange={(e) => setCertFormData({ ...certFormData, notes: e.target.value })} /></div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <Button type="button" variant="outline" size="sm" onClick={() => setShowCertForm(false)}>Cancel</Button>
                            <Button type="button" size="sm" onClick={handleSaveCert} className="bg-blue-500 hover:bg-blue-600" data-testid="save-cert-button">{editingCert ? 'Update' : 'Add'}</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {certifications.length === 0 ? (<div className="text-center py-8 text-slate-500"><Award className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p>No certifications recorded</p></div>) : (
                      <Table><TableHeader><TableRow><TableHead className="text-xs">Type</TableHead><TableHead className="text-xs">Name</TableHead><TableHead className="text-xs">Number</TableHead><TableHead className="text-xs">Training Start</TableHead><TableHead className="text-xs">Training End</TableHead><TableHead className="text-xs">Expiry</TableHead><TableHead className="text-xs">Status</TableHead><TableHead className="text-xs w-[80px]">Actions</TableHead></TableRow></TableHeader><TableBody>
                        {certifications.map((cert) => (
                          <TableRow key={cert.id}><TableCell className="text-sm">{cert.type}</TableCell><TableCell className="text-sm">{cert.name}</TableCell><TableCell className="text-sm font-mono">{cert.number || '-'}</TableCell><TableCell className="text-sm">{formatDisplayDate(cert.training_start_date) || '-'}</TableCell><TableCell className="text-sm">{formatDisplayDate(cert.training_finish_date) || '-'}</TableCell><TableCell className="text-sm">{formatDisplayDate(cert.expiry_date) || '-'}</TableCell><TableCell>{cert.status ? <Badge className={getStatusBadge(cert.status)}>{cert.status}</Badge> : <span className="text-slate-400 text-xs">-</span>}</TableCell><TableCell><div className="flex gap-1"><Button type="button" variant="ghost" size="sm" onClick={() => handleEditCert(cert)} className="h-8 w-8 p-0"><Pencil className="w-3 h-3" /></Button><Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteCert(cert.id)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button></div></TableCell></TableRow>
                        ))}
                      </TableBody></Table>
                    )}
                  </div>
                </TabsContent>
                
                {/* Medical Tab */}
                <TabsContent value="medical">
                  <div className="py-4">
                    <p className="text-sm text-slate-600 mb-4">Pre-Employment Medical and Drug & Alcohol Screening records</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* PEM Section */}
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-slate-700">Date of Last PEM</Label>
                        <Input type="date" data-testid="pem-date-input" value={medFormData.pem_date} onChange={(e) => setMedFormData({ ...medFormData, pem_date: e.target.value })} />
                        <p className="text-xs text-slate-500">Pre-Employment Medical check</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-slate-700">Results of PEM</Label>
                        <div className="flex gap-2" data-testid="pem-result-select">
                          {['green', 'orange', 'red'].map(r => (
                            <button key={r} type="button" onClick={() => setMedFormData({ ...medFormData, pem_result: medFormData.pem_result === r ? '' : r })}
                              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors capitalize ${medFormData.pem_result === r ? getPemBadge(r) + ' ring-2 ring-offset-1 ring-slate-300' : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'}`}
                            >{r}</button>
                          ))}
                        </div>
                      </div>

                      {/* LabDAS Section */}
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-slate-700">Date of Last LabDAS</Label>
                        <Input type="date" data-testid="labdas-date-input" value={medFormData.labdas_date} onChange={(e) => setMedFormData({ ...medFormData, labdas_date: e.target.value })} />
                        <p className="text-xs text-slate-500">Drug & Alcohol Screening</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-slate-700">Results of LabDAS</Label>
                        <div className="flex gap-2" data-testid="labdas-result-select">
                          {['negative', 'positive'].map(r => (
                            <button key={r} type="button" onClick={() => setMedFormData({ ...medFormData, labdas_result: medFormData.labdas_result === r ? '' : r })}
                              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors capitalize ${medFormData.labdas_result === r ? (r === 'negative' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 ring-2 ring-offset-1 ring-slate-300' : 'bg-red-50 text-red-700 border-red-200 ring-2 ring-offset-1 ring-slate-300') : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'}`}
                            >{r}</button>
                          ))}
                        </div>
                      </div>

                      {/* Project-Specific PEMs List */}
                      <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
                        <div className="flex justify-between items-center mb-3">
                          <Label className="text-sm font-semibold text-slate-700">Project-Specific PEMs</Label>
                          <Button type="button" size="sm" onClick={handleAddPem} data-testid="add-pem-button" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" />Add PEM</Button>
                        </div>
                        {showPemForm && (
                          <Card className="mb-3 border-teal-200 bg-teal-50">
                            <CardContent className="pt-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs font-semibold">Project *</Label>
                                  <select value={pemFormData.project} onChange={(e) => setPemFormData({ ...pemFormData, project: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm" data-testid="pem-project-select">
                                    <option value="">Select Project</option>
                                    {projects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                                  </select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs font-semibold">PEM Date</Label>
                                  <Input type="date" value={pemFormData.pem_date} onChange={(e) => setPemFormData({ ...pemFormData, pem_date: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs font-semibold">Result</Label>
                                  <div className="flex gap-1">
                                    {['green', 'orange', 'red'].map(r => (
                                      <button key={r} type="button" onClick={() => setPemFormData({ ...pemFormData, result: pemFormData.result === r ? '' : r })}
                                        className={`flex-1 px-2 py-1.5 text-xs font-medium rounded border capitalize ${pemFormData.result === r ? getPemBadge(r) + ' ring-1 ring-offset-1 ring-slate-300' : 'bg-white text-slate-500 border-slate-300'}`}
                                      >{r}</button>
                                    ))}
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs font-semibold">Notes</Label>
                                  <Input value={pemFormData.notes} onChange={(e) => setPemFormData({ ...pemFormData, notes: e.target.value })} placeholder="e.g., Audiometry required" />
                                </div>
                              </div>
                              <div className="flex justify-end gap-2 mt-3">
                                <Button type="button" variant="outline" size="sm" onClick={() => setShowPemForm(false)}>Cancel</Button>
                                <Button type="button" size="sm" onClick={handleSavePem} className="bg-teal-500 hover:bg-teal-600" data-testid="save-pem-button">{editingPem ? 'Update' : 'Add'}</Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                        {projectPems.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-4">No project-specific PEMs recorded</p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-xs">Project</TableHead>
                                <TableHead className="text-xs">Date</TableHead>
                                <TableHead className="text-xs">Result</TableHead>
                                <TableHead className="text-xs">Notes</TableHead>
                                <TableHead className="text-xs w-[80px]">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {projectPems.map(pem => (
                                <TableRow key={pem.id}>
                                  <TableCell className="text-sm font-medium">{pem.project}</TableCell>
                                  <TableCell className="text-sm">{formatDisplayDate(pem.pem_date) || '-'}</TableCell>
                                  <TableCell><Badge className={`text-xs capitalize ${getPemBadge(pem.result)}`}>{pem.result || '-'}</Badge></TableCell>
                                  <TableCell className="text-sm text-slate-500">{pem.notes || '-'}</TableCell>
                                  <TableCell>
                                    <div className="flex gap-1">
                                      <Button type="button" variant="ghost" size="sm" onClick={() => handleEditPem(pem)} className="h-8 w-8 p-0"><Pencil className="w-3 h-3" /></Button>
                                      <Button type="button" variant="ghost" size="sm" onClick={() => handleDeletePem(pem.id)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                {/* Inductions Tab */}
                <TabsContent value="inductions">
                  <div className="py-4">
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-sm text-slate-600">Manage site inductions</p>
                      <Button type="button" size="sm" onClick={handleAddInd} data-testid="add-induction-button" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" />Add Induction</Button>
                    </div>
                    {showIndForm && (
                      <Card className="mb-4 border-purple-200 bg-purple-50">
                        <CardContent className="pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1"><Label className="text-xs font-semibold">Site *</Label><Input value={indFormData.site} onChange={(e) => setIndFormData({ ...indFormData, site: e.target.value })} placeholder="Site or client name" data-testid="ind-site-input" /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Type</Label><select value={indFormData.induction_type} onChange={(e) => setIndFormData({ ...indFormData, induction_type: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"><option value="">Select Type</option>{inductionTypes.map(it => <option key={it.id} value={it.name}>{it.name}</option>)}<option value="General">General</option><option value="Other">Other</option></select></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Completed Date</Label><Input type="date" value={indFormData.completed_date} onChange={(e) => setIndFormData({ ...indFormData, completed_date: e.target.value })} /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Expiry Date</Label><Input type="date" value={indFormData.expiry_date} onChange={(e) => setIndFormData({ ...indFormData, expiry_date: e.target.value })} /></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Status</Label><select value={indFormData.status} onChange={(e) => setIndFormData({ ...indFormData, status: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"><option value="Active">Active</option><option value="Expired">Expired</option><option value="Pending">Pending</option></select></div>
                            <div className="space-y-1"><Label className="text-xs font-semibold">Notes</Label><Input value={indFormData.notes} onChange={(e) => setIndFormData({ ...indFormData, notes: e.target.value })} /></div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <Button type="button" variant="outline" size="sm" onClick={() => setShowIndForm(false)}>Cancel</Button>
                            <Button type="button" size="sm" onClick={handleSaveInd} className="bg-purple-500 hover:bg-purple-600" data-testid="save-induction-button">{editingInd ? 'Update' : 'Add'}</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {inductions.length === 0 ? (<div className="text-center py-8 text-slate-500"><ShieldCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p>No inductions recorded</p></div>) : (
                      <Table><TableHeader><TableRow><TableHead className="text-xs">Site</TableHead><TableHead className="text-xs">Type</TableHead><TableHead className="text-xs">Completed</TableHead><TableHead className="text-xs">Expiry</TableHead><TableHead className="text-xs">Status</TableHead><TableHead className="text-xs w-[80px]">Actions</TableHead></TableRow></TableHeader><TableBody>
                        {inductions.map((ind) => (
                          <TableRow key={ind.id}><TableCell className="text-sm">{ind.site}</TableCell><TableCell className="text-sm">{ind.induction_type}</TableCell><TableCell className="text-sm">{formatDisplayDate(ind.completed_date) || '-'}</TableCell><TableCell className="text-sm">{formatDisplayDate(ind.expiry_date) || '-'}</TableCell><TableCell><Badge className={getStatusBadge(ind.status)}>{ind.status}</Badge></TableCell><TableCell><div className="flex gap-1"><Button type="button" variant="ghost" size="sm" onClick={() => handleEditInd(ind)} className="h-8 w-8 p-0"><Pencil className="w-3 h-3" /></Button><Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteInd(ind.id)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button></div></TableCell></TableRow>
                        ))}
                      </TableBody></Table>
                    )}
                  </div>
                </TabsContent>

                {/* Sales Tab */}
                <TabsContent value="sales">
                  <div className="py-4">
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-sm text-slate-600">Manage suggested labour rates</p>
                      <Button type="button" size="sm" onClick={handleAddSalesRate} data-testid="add-sales-rate-button" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" />Add Rate</Button>
                    </div>
                    {showSalesForm && (
                      <Card className="mb-4 border-emerald-200 bg-emerald-50">
                        <CardContent className="pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Rate Type *</Label>
                              <select value={salesFormData.rate_type} onChange={(e) => setSalesFormData({ ...salesFormData, rate_type: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm" data-testid="sales-rate-type-select">
                                <option value="Hourly">Hourly</option>
                                <option value="Daily">Daily</option>
                                <option value="Monthly">Monthly</option>
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Rate Value *</Label>
                              <Input type="number" step="0.01" value={salesFormData.rate_value} onChange={(e) => setSalesFormData({ ...salesFormData, rate_value: e.target.value })} placeholder="e.g., 850.00" data-testid="sales-rate-value-input" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Currency</Label>
                              <select value={salesFormData.currency} onChange={(e) => setSalesFormData({ ...salesFormData, currency: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm">
                                <option value="AUD">AUD</option>
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                                <option value="SGD">SGD</option>
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Effective From</Label>
                              <Input type="date" value={salesFormData.effective_from} onChange={(e) => setSalesFormData({ ...salesFormData, effective_from: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Effective To</Label>
                              <Input type="date" value={salesFormData.effective_to} onChange={(e) => setSalesFormData({ ...salesFormData, effective_to: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Notes</Label>
                              <Input value={salesFormData.notes} onChange={(e) => setSalesFormData({ ...salesFormData, notes: e.target.value })} placeholder="e.g., Standard project rate" />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <Button type="button" variant="outline" size="sm" onClick={() => setShowSalesForm(false)}>Cancel</Button>
                            <Button type="button" size="sm" onClick={handleSaveSalesRate} className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-sales-rate-button">{editingSalesRate ? 'Update' : 'Add'}</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {salesRates.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <DollarSign className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p>No labour rates recorded</p>
                        <p className="text-xs mt-1">Add suggested labour rates for sales quotations</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Type</TableHead>
                            <TableHead className="text-xs">Rate</TableHead>
                            <TableHead className="text-xs">Currency</TableHead>
                            <TableHead className="text-xs">Effective From</TableHead>
                            <TableHead className="text-xs">Effective To</TableHead>
                            <TableHead className="text-xs">Notes</TableHead>
                            <TableHead className="text-xs w-[80px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {salesRates.map((rate) => (
                            <TableRow key={rate.id}>
                              <TableCell className="text-sm">{rate.rate_type}</TableCell>
                              <TableCell className="text-sm font-mono font-medium">{parseFloat(rate.rate_value || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-sm">{rate.currency}</TableCell>
                              <TableCell className="text-sm">{formatDisplayDate(rate.effective_from) || '-'}</TableCell>
                              <TableCell className="text-sm">{formatDisplayDate(rate.effective_to) || '-'}</TableCell>
                              <TableCell className="text-sm text-slate-500">{rate.notes || '-'}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button type="button" variant="ghost" size="sm" onClick={() => handleEditSalesRate(rate)} className="h-8 w-8 p-0"><Pencil className="w-3 h-3" /></Button>
                                  <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteSalesRate(rate.id)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </TabsContent>

                {/* Role History Tab */}
                <TabsContent value="role_history">
                  <div className="py-4">
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-sm text-slate-600">Track promotions and role changes with dates</p>
                      <Button type="button" size="sm" onClick={handleAddRoleHistory} data-testid="add-role-history-button" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" />Add Role</Button>
                    </div>
                    {showRoleHistoryForm && (
                      <Card className="mb-4 border-indigo-200 bg-indigo-50">
                        <CardContent className="pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Role *</Label>
                              <select value={roleHistoryFormData.role} onChange={(e) => setRoleHistoryFormData({ ...roleHistoryFormData, role: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm" data-testid="role-history-role-select">
                                <option value="">Select Role</option>
                                {roles.map((r) => (<option key={r.id} value={r.name}>{r.name}</option>))}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Notes</Label>
                              <Input value={roleHistoryFormData.notes} onChange={(e) => setRoleHistoryFormData({ ...roleHistoryFormData, notes: e.target.value })} placeholder="e.g., Promoted from Operator" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Start Date</Label>
                              <Input type="date" value={roleHistoryFormData.start_date} onChange={(e) => setRoleHistoryFormData({ ...roleHistoryFormData, start_date: e.target.value })} data-testid="role-history-start-input" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">End Date</Label>
                              <Input type="date" value={roleHistoryFormData.end_date} onChange={(e) => setRoleHistoryFormData({ ...roleHistoryFormData, end_date: e.target.value })} data-testid="role-history-end-input" />
                              <p className="text-xs text-slate-500">Leave blank if current role</p>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <Button type="button" variant="outline" size="sm" onClick={() => setShowRoleHistoryForm(false)}>Cancel</Button>
                            <Button type="button" size="sm" onClick={handleSaveRoleHistory} className="bg-indigo-500 hover:bg-indigo-600" data-testid="save-role-history-button">{editingRoleHistory ? 'Update' : 'Add'}</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {roleHistory.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <History className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p>No role history recorded</p>
                        <p className="text-xs mt-1">Track promotions and role changes over time</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Role</TableHead>
                            <TableHead className="text-xs">Start Date</TableHead>
                            <TableHead className="text-xs">End Date</TableHead>
                            <TableHead className="text-xs">Notes</TableHead>
                            <TableHead className="text-xs w-[80px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {roleHistory.sort((a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0)).map((entry) => (
                            <TableRow key={entry.id}>
                              <TableCell className="text-sm font-medium">{entry.role}</TableCell>
                              <TableCell className="text-sm">{formatDisplayDate(entry.start_date) || '-'}</TableCell>
                              <TableCell className="text-sm">{entry.end_date ? formatDisplayDate(entry.end_date) : <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Current</Badge>}</TableCell>
                              <TableCell className="text-sm text-slate-500">{entry.notes || '-'}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button type="button" variant="ghost" size="sm" onClick={() => handleEditRoleHistory(entry)} className="h-8 w-8 p-0"><Pencil className="w-3 h-3" /></Button>
                                  <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteRoleHistory(entry.id)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </TabsContent>
                <TabsContent value="visa_history">
                  <div className="py-4">
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-sm text-slate-600">Track current and previous visas. The most recent entry auto-syncs to General → Visa Type &amp; Expiry.</p>
                      <Button type="button" size="sm" onClick={handleAddVisaHistory} data-testid="add-visa-history-button" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" />Add Visa</Button>
                    </div>
                    {showVisaHistoryForm && (
                      <Card className="mb-4 border-blue-200 bg-blue-50">
                        <CardContent className="pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Visa Type *</Label>
                              <select value={visaHistoryFormData.visa_type} onChange={(e) => setVisaHistoryFormData({ ...visaHistoryFormData, visa_type: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm" data-testid="visa-history-type-select">
                                <option value="">Select Visa Type</option>
                                {visaTypes.map((vt) => (<option key={vt.id} value={vt.name}>{vt.name}</option>))}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Country</Label>
                              <Input value={visaHistoryFormData.country} onChange={(e) => setVisaHistoryFormData({ ...visaHistoryFormData, country: e.target.value })} placeholder="e.g., Australia" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Start Date</Label>
                              <Input type="date" value={visaHistoryFormData.start_date} onChange={(e) => setVisaHistoryFormData({ ...visaHistoryFormData, start_date: e.target.value })} data-testid="visa-history-start-input" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Expiry Date</Label>
                              <Input type="date" value={visaHistoryFormData.expiry_date} onChange={(e) => setVisaHistoryFormData({ ...visaHistoryFormData, expiry_date: e.target.value })} data-testid="visa-history-expiry-input" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Document Number</Label>
                              <Input value={visaHistoryFormData.document_number} onChange={(e) => setVisaHistoryFormData({ ...visaHistoryFormData, document_number: e.target.value })} placeholder="e.g., V123456789" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-semibold">Notes</Label>
                              <Input value={visaHistoryFormData.notes} onChange={(e) => setVisaHistoryFormData({ ...visaHistoryFormData, notes: e.target.value })} placeholder="e.g., Sponsored by Fagioli SPA" />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <Button type="button" variant="outline" size="sm" onClick={() => setShowVisaHistoryForm(false)}>Cancel</Button>
                            <Button type="button" size="sm" onClick={handleSaveVisaHistory} className="bg-blue-600 hover:bg-blue-700" data-testid="save-visa-history-button">{editingVisaHistory ? 'Update' : 'Add'}</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {visaHistory.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <CreditCard className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p>No visa history recorded</p>
                        <p className="text-xs mt-1">Track current and previous visas over time</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Visa Type</TableHead>
                            <TableHead className="text-xs">Country</TableHead>
                            <TableHead className="text-xs">Start Date</TableHead>
                            <TableHead className="text-xs">Expiry Date</TableHead>
                            <TableHead className="text-xs">Document #</TableHead>
                            <TableHead className="text-xs">Notes</TableHead>
                            <TableHead className="text-xs w-[80px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visaHistory.slice().sort((a, b) => new Date(b.expiry_date || 0) - new Date(a.expiry_date || 0)).map((entry, idx) => {
                            const isCurrent = idx === 0;
                            const expiry = entry.expiry_date ? new Date(entry.expiry_date) : null;
                            const isExpired = expiry && expiry < new Date();
                            return (
                              <TableRow key={entry.id}>
                                <TableCell className="text-sm font-medium">{entry.visa_type}</TableCell>
                                <TableCell className="text-sm">{entry.country || '-'}</TableCell>
                                <TableCell className="text-sm">{formatDisplayDate(entry.start_date) || '-'}</TableCell>
                                <TableCell className="text-sm">
                                  {formatDisplayDate(entry.expiry_date) || '-'}
                                  {isCurrent && !isExpired && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs ml-2">Current</Badge>}
                                  {isExpired && <Badge className="bg-red-50 text-red-700 border-red-200 text-xs ml-2">Expired</Badge>}
                                </TableCell>
                                <TableCell className="text-sm text-slate-500">{entry.document_number || '-'}</TableCell>
                                <TableCell className="text-sm text-slate-500">{entry.notes || '-'}</TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button type="button" variant="ghost" size="sm" onClick={() => handleEditVisaHistory(entry)} className="h-8 w-8 p-0"><Pencil className="w-3 h-3" /></Button>
                                    <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteVisaHistory(entry.id)} className="h-8 w-8 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </TabsContent>

              </Tabs>
              </form>
              
              <SheetFooter className="mt-4">
                <SheetClose asChild><Button type="button" variant="outline" data-testid="cancel-button">Cancel</Button></SheetClose>
                <Button type="submit" form="personnel-form" data-testid="save-personnel-button" className="bg-slate-900 hover:bg-slate-800">{editingItem ? 'Update' : 'Create'}</Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
          )}
        </div>
      </div>

      <Card className="bg-white border-slate-200 shadow-sm" data-testid="personnel-table">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">
                {showTrash ? 'Trash' : 'Personnel List'}
              </CardTitle>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowTrash(!showTrash); setSelectedIds(new Set()); }}
                  data-testid="toggle-trash-button"
                  className={showTrash ? "border-slate-300 text-slate-700 font-medium" : "border-slate-300 text-slate-600"}
                >
                  {showTrash ? (
                    <><ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Back to Personnel List</>
                  ) : (
                    <><Trash2 className="w-3.5 h-3.5 mr-1.5" />Trash ({trashedPersonnel.length})</>
                  )}
                </Button>
              )}
            </div>
            <span className="text-xs text-slate-500" data-testid="personnel-count">
              {showTrash 
                ? `${trashedPersonnel.length} in trash`
                : (visaExpiringFilter
                    ? `Showing ${filteredPersonnel.length} of ${personnel.length} personnel`
                    : `Showing ${personnel.length} of ${totalCount} personnel`)}
            </span>
          </div>
          {/* Bulk action bar */}
          {canEdit && !showTrash && selectedIds.size > 0 && (
            <div className="mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
              <span className="text-sm font-medium text-amber-800">{selectedIds.size} selected</span>
              <Button size="sm" variant="destructive" onClick={handleBulkDelete} data-testid="bulk-delete-button" className="h-7">
                <Trash2 className="w-3.5 h-3.5 mr-1" />Move to Trash
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="h-7 text-slate-600">
                Clear Selection
              </Button>
            </div>
          )}
          {showTrash && trashedPersonnel.length > 0 && canEdit && (
            <div className="mt-3 flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-700">Items in trash will remain until permanently deleted.</span>
              <Button size="sm" variant="destructive" onClick={handleEmptyTrash} data-testid="empty-trash-button" className="h-7 ml-auto">
                Empty Trash
              </Button>
            </div>
          )}
          {!showTrash && (
          <div className="mt-3">
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => setVisaExpiringFilter(v => !v)}
                data-testid="visa-expiring-quick-filter"
                className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-xs font-medium transition-colors ${
                  visaExpiringFilter
                    ? 'bg-amber-500 border-amber-500 text-white shadow-sm hover:bg-amber-600'
                    : 'bg-white border-slate-300 text-slate-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700'
                }`}
                title="One-click filter: Personnel whose current visa expires in 30 days or less (includes already expired)"
              >
                <AlertTriangle className="w-3 h-3" />
                <span>Visa Expiring ≤30 days</span>
                <span className={`ml-1 px-1.5 rounded-full text-[10px] ${visaExpiringFilter ? 'bg-white/25' : 'bg-amber-100 text-amber-700'}`}>
                  {visaExpiringSoonCount}
                </span>
              </button>
              {visaExpiringFilter && (
                <span className="text-xs text-amber-700 font-medium" data-testid="visa-expiring-active-indicator">
                  Showing only personnel with visa expiring within 30 days
                </span>
              )}
            </div>
            <TableViewToolbar {...personnelTableView} columns={personnelColumns} />
          </div>
          )}
        </CardHeader>
        <CardContent>
          {showTrash ? (
            /* Trash view */
            trashedPersonnel.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Trash2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Trash is empty</p>
                <p className="text-xs mt-1">Deleted personnel will appear here</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-mono text-xs uppercase">Name</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Role</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Deleted By</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Deleted At</TableHead>
                    <TableHead className="font-mono text-xs uppercase w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trashedPersonnel.map((item) => (
                    <TableRow key={item.id} className="opacity-70">
                      <TableCell className="font-medium text-sm">{item.name}</TableCell>
                      <TableCell className="text-sm">{item.role}</TableCell>
                      <TableCell className="text-sm text-slate-500">{item.deleted_by || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-500">{item.deleted_at ? formatDisplayDate(item.deleted_at) : '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleRestore(item.id)} data-testid={`restore-${item.id}`} className="h-7 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                            <RotateCcw className="w-3 h-3 mr-1" />Restore
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handlePermanentDelete(item.id)} data-testid={`perm-delete-${item.id}`} className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
          /* Active personnel view */
          <Table>
            <TableHeader>
              <TableRow>
                {canEdit && (
                  <TableHead className="w-[40px]">
                    <button type="button" onClick={toggleSelectAll} data-testid="select-all-checkbox" className="flex items-center justify-center">
                      {selectedIds.size === filteredPersonnel.length && filteredPersonnel.length > 0
                        ? <CheckSquare className="w-4 h-4 text-slate-700" />
                        : <Square className="w-4 h-4 text-slate-400" />}
                    </button>
                  </TableHead>
                )}
                {personnelTableView.isColumnVisible('name') && <SortableHead field="name" label="Name" sortBy={personnelTableView.sortBy} sortDir={personnelTableView.sortDir} onSort={personnelTableView.toggleSort} />}
                {personnelTableView.isColumnVisible('role') && <SortableHead field="role" label="Role" sortBy={personnelTableView.sortBy} sortDir={personnelTableView.sortDir} onSort={personnelTableView.toggleSort} />}
                {personnelTableView.isColumnVisible('employer') && <SortableHead field="employer" label="Employer" sortBy={personnelTableView.sortBy} sortDir={personnelTableView.sortDir} onSort={personnelTableView.toggleSort} />}
                {personnelTableView.isColumnVisible('contract_type') && <SortableHead field="contract_type" label="Contract" sortBy={personnelTableView.sortBy} sortDir={personnelTableView.sortDir} onSort={personnelTableView.toggleSort} />}
                {personnelTableView.isColumnVisible('skills') && <TableHead className="font-mono text-xs uppercase">Skills</TableHead>}
                {personnelTableView.isColumnVisible('visa') && <TableHead className="font-mono text-xs uppercase">Visa</TableHead>}
                {personnelTableView.isColumnVisible('next_fifo') && <TableHead className="font-mono text-xs uppercase">Next FIFO</TableHead>}
                {personnelTableView.isColumnVisible('compliance') && <TableHead className="font-mono text-xs uppercase">Compliance</TableHead>}
                {personnelTableView.isColumnVisible('current_allocation') && <TableHead className="font-mono text-xs uppercase">Allocation</TableHead>}
                {personnelTableView.isColumnVisible('modified_by') && <TableHead className="font-mono text-xs uppercase">Modified By</TableHead>}
                {canEdit && <TableHead className="font-mono text-xs uppercase w-[60px]">Actions</TableHead>}
              </TableRow>
              <ColumnFilterRow columns={personnelColumns} columnFilters={personnelTableView.columnFilters} setColumnFilter={personnelTableView.setColumnFilter} isColumnVisible={personnelTableView.isColumnVisible} leadingCells={canEdit ? 1 : 0} />
            </TableHeader>
            <TableBody>
              {filteredPersonnel.map((item) => {
                const nextFifo = getNextFifo(item);
                const compliance = getComplianceSummary(item);
                return (
                  <TableRow key={item.id} data-testid={`personnel-row-${item.id}`} className="cursor-pointer hover:bg-slate-50" onClick={(e) => handleRowClick(item, e)}>
                    {canEdit && (
                      <TableCell>
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggleSelect(item.id); }} data-testid={`select-${item.id}`} className="flex items-center justify-center">
                          {selectedIds.has(item.id)
                            ? <CheckSquare className="w-4 h-4 text-slate-700" />
                            : <Square className="w-4 h-4 text-slate-400" />}
                        </button>
                      </TableCell>
                    )}
                    {personnelTableView.isColumnVisible('name') && <TableCell className="font-medium text-sm">{item.name}</TableCell>}
                    {personnelTableView.isColumnVisible('role') && <TableCell className="text-sm">{item.role}</TableCell>}
                    {personnelTableView.isColumnVisible('employer') && <TableCell className="text-sm">{item.employer || '-'}</TableCell>}
                    {personnelTableView.isColumnVisible('contract_type') && <TableCell className="text-sm">{item.contract_type || '-'}</TableCell>}
                    {personnelTableView.isColumnVisible('skills') && (
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(item.skills || []).slice(0, 2).map((skill, idx) => (<Badge key={idx} className="bg-slate-100 text-slate-700 border-slate-200 text-xs">{skill}</Badge>))}
                          {(item.skills || []).length > 2 && (<Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">+{(item.skills || []).length - 2}</Badge>)}
                        </div>
                      </TableCell>
                    )}
                    {personnelTableView.isColumnVisible('visa') && (() => {
                      const v = getCurrentVisaForRow(item);
                      const dotClass = getVisaColor(v?.expiry_date);
                      return (
                        <TableCell className="text-xs">
                          {v ? (
                            <div className="flex items-center gap-1.5" title={v.expiry_date ? `Expires ${formatDisplayDate(v.expiry_date)}` : ''}>
                              <span className={`inline-block w-2 h-2 rounded-full ${dotClass}`} />
                              <span className="text-slate-700">{v.visa_type || 'Visa'}</span>
                              {v.expiry_date && (
                                <span className="text-slate-400">· {formatDisplayDate(v.expiry_date)}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                      );
                    })()}
                    {personnelTableView.isColumnVisible('next_fifo') && (
                      <TableCell className="text-sm">
                        {nextFifo ? (<div className="flex items-center gap-1"><Plane className="w-3 h-3 text-amber-500" /><span className="text-xs">{formatDisplayDate(nextFifo.fly_in_date)}</span></div>) : (<span className="text-slate-400 text-xs">-</span>)}
                      </TableCell>
                    )}
                    {personnelTableView.isColumnVisible('compliance') && (
                      <TableCell>
                        {compliance.total > 0 ? (
                          <div className="flex items-center gap-2 text-xs">
                            {compliance.certs > 0 && <span className="flex items-center gap-1"><Award className="w-3 h-3 text-blue-500" />{compliance.certs}</span>}
                            {compliance.inds > 0 && <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-purple-500" />{compliance.inds}</span>}
                          </div>
                        ) : (<span className="text-slate-400 text-xs">-</span>)}
                      </TableCell>
                    )}
                    {personnelTableView.isColumnVisible('current_allocation') && <TableCell className="text-sm">{item.current_allocation || '-'}</TableCell>}
                    {personnelTableView.isColumnVisible('modified_by') && <TableCell><LastModifiedBadge createdBy={item.created_by} updatedBy={item.updated_by} updatedAt={item.updated_at} /></TableCell>}
                    {canEdit && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild data-actions-menu><Button variant="ghost" className="h-8 w-8 p-0"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(item)}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          )}
          {!showTrash && !listLoading && personnel.length === 0 && personnelHasActiveFilters && (
            <div className="text-center py-8 text-slate-500">
              <p className="text-sm">No personnel match your filters</p>
            </div>
          )}
          {!showTrash && !listLoading && personnel.length === 0 && !personnelHasActiveFilters && (
            <p className="text-center text-slate-500 py-8 text-sm">No personnel found</p>
          )}
          {!showTrash && listLoading && personnel.length === 0 && (
            <p className="text-center text-slate-400 py-8 text-sm">Loading personnel…</p>
          )}
          {!showTrash && !visaExpiringFilter && hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-4">
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="personnel-load-more">
                {loadingMore ? 'Loading…' : `Load more (${personnel.length} of ${totalCount})`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AuditTrailPanel open={auditOpen} onOpenChange={setAuditOpen} entityType="personnel" title="Personnel Activity Log" />
    </div>
  );
}
